import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Shown after any successful submit, regardless of whether the email
  // is actually registered — the backend deliberately gives the same
  // response either way so this page can't be used to check who has an
  // account.
  const [submitted, setSubmitted] = useState(false);
  // Whether reset-by-email is switched on (System Settings). null while
  // finding out. When off — the default — the page explains how to get a
  // temporary password instead of offering a link that won't be sent.
  const [emailEnabled, setEmailEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    apiService
      .getPasswordResetEmailEnabled()
      .then(setEmailEnabled)
      // Can't tell: show the route that always works.
      .catch(() => setEmailEnabled(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await apiService.requestPasswordReset(email);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to request password reset');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Forgot your password?</h2>

        {emailEnabled === null ? (
          <p>Loading...</p>
        ) : !emailEnabled ? (
          <>
            <p style={{ marginBottom: '1rem' }}>
              Ask the <strong>scholarship office</strong> for a temporary password:
            </p>
            <ol style={{ margin: '0 0 1rem', paddingLeft: '1.25rem', lineHeight: 1.6 }}>
              <li>Contact or visit the scholarship office and give them the email address you registered with.</li>
              <li>They will give you a <strong>temporary password</strong>.</li>
              <li>
                <Link to="/login">Sign in</Link> with your email and that temporary password.
              </li>
              <li>You will be asked to choose a <strong>new password of your own</strong> right away.</li>
            </ol>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
              Keep the temporary password to yourself — anyone who has it can sign in as you until you change it.
            </p>
          </>
        ) : (
          <>
            <p>Enter your account email and we'll send you a link to reset your password.</p>

            {error && (
              <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                {error}
              </div>
            )}

            {submitted ? (
              <p>
                If that email is registered, a password reset link has been sent — check your inbox (and spam
                folder). The link expires in 1 hour.
              </p>
            ) : (
              <form className="form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button className="btn btn-primary btn-lg" type="submit" disabled={isLoading} style={{ width: '100%' }}>
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>
            )}
          </>
        )}

        <p style={{ margin: '1.5rem 0 0', textAlign: 'center', fontSize: '0.9rem' }}>
          <Link to="/login">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
